import { type NextPage } from 'next'
import BlackBird from 'shared/blackbird'

import { Loader } from '~/domains/platform/design-system/loader/loader'
import { Choose } from '~/domains/platform/lib/utilities-components'
import { CleanPageContainer } from '~/ui/containers/clean-page-container'

import { useCredentials } from '../deus-ex-machina/hooks/use-credentials'
import { type WithDeusExMachinaProps } from '../deus-ex-machina/types'

function isKycPending({ subject, entity }: WithDeusExMachinaProps) {
  const isOneStoneKyc = Boolean(subject.kyc ?? entity?.stoneAccount?.kyc)
  if (isOneStoneKyc) return Boolean(subject.kyc?.shouldRedirect || entity?.stoneAccount?.kyc?.shouldRedirect)

  const legacyKyc = subject?.kycRequest?.status
  return legacyKyc !== 'approved'
}

export function withPinRegister(Page: NextPage<WithDeusExMachinaProps>) {
  function WrappedPage({ subject, ...rest }: WithDeusExMachinaProps) {
    const query = BlackBird.getQuery()
    const pinConfiguredFromCache = subject.credentials.includes('pin')
    const isKycPendingStatus = isKycPending({ subject, ...rest })

    const {
      credentials: freshCredentials,
      isLoading: isLoadingCredentials,
      isError: isCredentialsError
    } = useCredentials({ enabled: !pinConfiguredFromCache })

    const pinConfigured = pinConfiguredFromCache || freshCredentials.includes('pin')
    const isWaitingForFreshCredentials = !pinConfiguredFromCache && isLoadingCredentials && !isCredentialsError

    return (
      <Choose>
        <Choose.When condition={Boolean(pinConfigured) || isKycPendingStatus}>
          <Page subject={subject} {...rest} />
        </Choose.When>
        <Choose.When condition={isWaitingForFreshCredentials}>
          <CleanPageContainer>
            <Loader />
          </CleanPageContainer>
        </Choose.When>
        <Choose.Otherwise
          render={() => {
            const { pin } = query as Record<string, string>
            if (pin !== 'success' && !isCredentialsError) BlackBird.travelTo('/pin')
            return (
              <CleanPageContainer>
                <Loader />
              </CleanPageContainer>
            )
          }}
        />
      </Choose>
    )
  }

  WrappedPage.displayName = `withPinRegister(${Page.displayName})`

  return WrappedPage
}
